import { Field } from "src/shared/contexts/HackerContext/types";
import { Nullable } from "src/shared/utils/typescript";

export type BadgeNetworkingAnswers = {
  [Field.NETWORKING_PHONE]: Nullable<string>;
  [Field.NETWORKING_EMAIL]: Nullable<string>;
  [Field.LINKEDIN]: Nullable<string>;
  [Field.INSTAGRAM]: Nullable<string>;
  [Field.TWITTER]: Nullable<string>;
  [Field.DISCORD_TAG]: Nullable<string>;
};

export type BadgeNetworkingPrefillSources = {
  personalPhone?: Nullable<string>;
  accountEmail?: Nullable<string>;
  longLinks?: Nullable<string[] | Record<string, string>>;
  hasCompletedBadge?: boolean;
};

const isNonEmptyString = (value: Nullable<string> | undefined): boolean =>
  value != null && value.trim() !== "";

const flattenLongLinks = (
  longLinks: BadgeNetworkingPrefillSources["longLinks"]
): string[] => {
  if (longLinks == null) return [];
  if (Array.isArray(longLinks)) {
    return longLinks.filter(
      (link): link is string => typeof link === "string" && link.trim() !== ""
    );
  }
  return Object.values(longLinks).filter(
    (link): link is string => typeof link === "string" && link.trim() !== ""
  );
};

/** Pick the first long_links entry whose host matches one of the domains. */
const findSocialLink = (links: string[], domains: string[]): string | null => {
  for (const link of links) {
    const trimmed = link.trim();
    const hostMatch = domains.some((domain) =>
      new RegExp(`(^|\\/\\/)(www\\.)?${domain}\\.com(\\/|$)`, "i").test(trimmed)
    );
    if (hostMatch) return trimmed;
  }
  return null;
};

/**
 * Before the Badge step is completed, fill each empty networking field from a
 * known source without overwriting any existing claim answer. Once completed,
 * return the saved values unchanged so intentionally cleared fields stay clear.
 */
export const buildBadgeNetworkingValues = (
  current: BadgeNetworkingAnswers,
  sources: BadgeNetworkingPrefillSources
): BadgeNetworkingAnswers => {
  if (sources.hasCompletedBadge) {
    return current;
  }

  const links = flattenLongLinks(sources.longLinks);
  const phone = sources.personalPhone?.trim() || null;
  const email = sources.accountEmail?.trim() || null;

  return {
    [Field.NETWORKING_PHONE]: isNonEmptyString(current[Field.NETWORKING_PHONE])
      ? current[Field.NETWORKING_PHONE]
      : phone,
    [Field.NETWORKING_EMAIL]: isNonEmptyString(current[Field.NETWORKING_EMAIL])
      ? current[Field.NETWORKING_EMAIL]
      : email,
    [Field.LINKEDIN]: isNonEmptyString(current[Field.LINKEDIN])
      ? current[Field.LINKEDIN]
      : findSocialLink(links, ["linkedin"]),
    [Field.INSTAGRAM]: isNonEmptyString(current[Field.INSTAGRAM])
      ? current[Field.INSTAGRAM]
      : findSocialLink(links, ["instagram"]),
    [Field.TWITTER]: isNonEmptyString(current[Field.TWITTER])
      ? current[Field.TWITTER]
      : findSocialLink(links, ["x", "twitter"]),
    [Field.DISCORD_TAG]: current[Field.DISCORD_TAG],
  };
};
